/**
 * Moving a table out of the database in chunks (infrastructure/db-helper/DESIGN_EDITING.md E12, console flow 10):
 * export_start stages a CSV on the database's machine, then the console reads it back 1 MiB at a time and saves it.
 *
 * ⚠️ NO IMPORTS, ON PURPOSE (dataView.ts says why): the flow takes its `get` from the caller, so it is tested without
 *    axios. Every answer is checked before a byte of it is kept: the export it names, the offset, and that its base64
 *    decodes to exactly the length it claims.
 */

/** At most this many calls for one export: export_start, then 50 chunks of 1 MiB (the backend's 50 MiB cap). */
export const EXPORT_MAX_REQUESTS = 51;
export const EXPORT_MAX_BYTES = 50 * 1024 * 1024;

const EXPORT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const BASE64_RE = /^[A-Za-z0-9+/]*={0,2}$/;

const isCount = (n: unknown, max: number): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= max;

export interface ExportStart { exportId: string; bytes: number; rows: number }

/** export_start's answer, or null when it is not that shape. */
export const readExportStart = (data: any): ExportStart | null => {
    if (!data || typeof data.export_id !== 'string' || !EXPORT_ID_RE.test(data.export_id)) return null;
    if (!isCount(data.bytes, EXPORT_MAX_BYTES) || data.bytes < 3 || !isCount(data.rows, Number.MAX_SAFE_INTEGER)) return null;
    return { exportId: data.export_id, bytes: data.bytes, rows: data.rows };
};

/** Base64 to bytes (the browser's own atob), or null when it is not base64. */
export const base64ToBytes = (s: string): Uint8Array | null => {
    if (s.length % 4 !== 0 || !BASE64_RE.test(s)) return null;
    let bin: string;
    try {
        bin = atob(s);
    } catch {
        return null;
    }
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
};

export interface ExportChunk { bytes: Uint8Array; total: number; done: boolean }

/** One export_read answer for this export at this offset, or null when it is not exactly that. */
export const readExportChunk = (data: any, exportId: string, offset: number, expectedTotal: number): ExportChunk | null => {
    if (!data || data.export_id !== exportId || data.offset !== offset || data.total !== expectedTotal) return null;
    if (!isCount(data.bytes, 1024 * 1024) || typeof data.done !== 'boolean' || typeof data.data !== 'string') return null;
    const bytes = base64ToBytes(data.data);
    if (!bytes || bytes.length !== data.bytes) return null;
    if (offset + bytes.length > expectedTotal || data.done !== (offset + bytes.length === expectedTotal)) return null;
    return { bytes, total: expectedTotal, done: data.done };
};

/**
 * The export call for one table, or null when its name cannot travel in a URL path (a browser removes a `.` or `..`
 * segment even as %2e: dataView.ts rowsUrl).
 */
export const exportUrl = (databaseId: number, schema: string, table: string): string | null => {
    if ([schema, table].some((s) => s === '.' || s === '..')) return null;
    return `/v1/databases/${databaseId}/data/tables/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/export`;
};

export const exportChunkUrl = (databaseId: number, exportId: string): string =>
    `/v1/databases/${databaseId}/data/exports/${encodeURIComponent(exportId)}`;

/** `<table>.csv`, any character outside A-Za-z0-9._- turned into `_` (console flow 10), never a hidden file. */
export const exportFileName = (table: string): string => {
    const safe = table.replace(/[^A-Za-z0-9._-]/g, '_').replace(/^\./, '_');
    return `${safe || 'table'}.csv`;
};

/** "Exporting 45%": the share of the staged file read so far. */
export const exportProgress = (received: number, total: number): string =>
    `Exporting ${total > 0 ? Math.min(100, Math.floor((received / total) * 100)) : 0}%`;

export type ExportOutcome =
    | { ok: true; parts: Uint8Array[]; fileName: string; rows: number; bytes: number }
    | { ok: false; reason: 'too_large' | 'failed' | 'unaddressable'; code?: string };

/** The API's error code on a rejected call (an axios error), duck-typed. */
const codeOf = (err: any): string | undefined => {
    const c = err?.response?.data?.error?.code;
    return typeof c === 'string' ? c : undefined;
};

/**
 * The whole export: export_start, then the chunks in order until done, never more than EXPORT_MAX_REQUESTS calls or
 * EXPORT_MAX_BYTES. `get` is the caller's axios GET (30 s timeout, no Idempotency-Key) and answers the response body.
 */
export async function runExport(
    get: (url: string, params?: Record<string, number>) => Promise<any>,
    databaseId: number, schema: string, table: string,
    onProgress: (received: number, total: number) => void,
    /** Checked before every call: true stops the export (the view was left; review round 2, 2026-10-24). */
    cancelled: () => boolean = () => false,
): Promise<ExportOutcome> {
    const url = exportUrl(databaseId, schema, table);
    if (!url) return { ok: false, reason: 'unaddressable' };
    const fail = (err: any): ExportOutcome => {
        const code = codeOf(err);
        return { ok: false, reason: code === 'export_too_large' ? 'too_large' : 'failed', code };
    };
    let start: ExportStart | null;
    try {
        start = readExportStart(await get(url));
    } catch (err) {
        return fail(err);
    }
    if (!start) return { ok: false, reason: 'failed', code: 'bad_answer' };
    const parts: Uint8Array[] = [];
    let offset = 0;
    onProgress(0, start.bytes);
    for (let calls = 1; calls < EXPORT_MAX_REQUESTS; calls++) {
        if (cancelled()) return { ok: false, reason: 'failed', code: 'cancelled' };
        let chunk: ExportChunk | null;
        try {
            chunk = readExportChunk(await get(exportChunkUrl(databaseId, start.exportId), { offset }), start.exportId, offset, start.bytes);
        } catch (err) {
            return fail(err);
        }
        if (!chunk) return { ok: false, reason: 'failed', code: 'bad_answer' };
        parts.push(chunk.bytes);
        offset += chunk.bytes.length;
        onProgress(offset, start.bytes);
        if (chunk.done) return { ok: true, parts, fileName: exportFileName(table), rows: start.rows, bytes: offset };
        if (chunk.bytes.length === 0) return { ok: false, reason: 'failed', code: 'no_progress' };
    }
    return { ok: false, reason: 'failed', code: 'too_many_requests' };
}

/** The line the console shows for a failed export (console flow 10). */
export const exportFailureLine = (o: { reason: 'too_large' | 'failed' | 'unaddressable' }): string =>
    (o.reason === 'too_large' ? 'Too large to export here.' : o.reason === 'unaddressable' ? "This table can't be exported here." : 'Export failed.');
